import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Group,
  Title,
  Paper,
  Loader,
  Center,
} from '@mantine/core';
import { IconCash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Invoice, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useLocation } from 'react-router-dom';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import { ResourceTable } from '@/components/resource-search/ResourceTable';
import {
  usePaymentSearch,
  PAYMENT_SEARCH_DEFAULTS,
  PaymentSearchParams,
} from './usePaymentSearch';
import {
  getPaymentSearchFields,
  getPaymentTableColumns,
} from './paymentSearchConfig';
import { InvoiceDetailModal } from './InvoiceDetailModal';
import { PaymentSummaryCards } from './PaymentSummaryCards';
import { useDebounceSearch } from '@/pages/ainigma/lims/hooks/useDebounceSearch';
import { getLinkedOrderRef } from '../utils/payment-helpers';
import { getOrderIdSuggestions } from '../utils/fhir-helpers';

// -----------------------------------------------------------------------------
export const PaymentsPage: FC = () => {
  const medplum = useMedplum();
  const location = useLocation();
  const { t } = useTranslation('lims');

  // Use the payment search hook
  const {
    resources: invoices,
    loading,
    page,
    totalPages,
    totalCount,
    setPage,
    patients,
    searchParams,
    updateSearchParams,
    clearSearch,
    fetchResources: fetchInvoices,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
  } = usePaymentSearch(medplum);

  // Modal state
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Order ID autocomplete (reuse the same suggestion logic as the orders page)
  const {
    query: orderIdQuery,
    setQuery: setOrderIdQuery,
    results: orderIdOptions,
    loading: orderIdLoading,
  } = useDebounceSearch<string>({
    searchFunction: (q: string) => getOrderIdSuggestions(medplum, q),
  });

  // ServiceRequest map: fhirId -> ServiceRequest, built from loaded invoices
  const [serviceRequests, setServiceRequests] = useState<Map<string, ServiceRequest>>(new Map());

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fetch linked ServiceRequests whenever the invoice list changes
  useEffect(() => {
    const refs = invoices
      .map((inv) => getLinkedOrderRef(inv))
      .filter((ref): ref is string => Boolean(ref));
    const unique = Array.from(new Set(refs));
    if (unique.length === 0) {
      setServiceRequests(new Map());
      return;
    }
    Promise.all(
      unique.map((ref) =>
        medplum.readReference({ reference: ref } as any).catch(() => null)
      )
    ).then((results) => {
      const map = new Map<string, ServiceRequest>();
      results.forEach((sr, i) => {
        if (sr) {
          const id = unique[i].replace('ServiceRequest/', '');
          map.set(id, sr as ServiceRequest);
        }
      });
      setServiceRequests(map);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  const handleSearch = useCallback(
    (updates: Partial<PaymentSearchParams>) => {
      const merged = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchInvoices(merged);
    },
    [fetchInvoices, searchParams, setPage, updateSearchParams]
  );

  const handleClearSearch = useCallback(() => {
    clearSearch(PAYMENT_SEARCH_DEFAULTS);
  }, [clearSearch]);

  // ---------------------------------------------------------------------------
  const fields = useMemo(
    () =>
      getPaymentSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
        orderIdQuery,
        setOrderIdQuery,
        orderIdOptions,
        orderIdLoading,
      }),
    [
      handleSearch,
      orderIdLoading,
      orderIdOptions,
      orderIdQuery,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setOrderIdQuery,
      setPatientNameQuery,
      setPatientQuery,
      t,
    ]
  );

  const columns = useMemo(
    () =>
      getPaymentTableColumns({
        t,
        patients,
        serviceRequests,
      }),
    [patients, serviceRequests, t]
  );

  const handleInvoiceClick = useCallback(
    (invoice: Invoice) => {
      setSelectedInvoice(invoice);
      // Resolve the patient for the modal
      const subjectRef = invoice.subject?.reference;
      if (subjectRef) {
        const patientId = subjectRef.replace('Patient/', '');
        const pat = patients.get(patientId) || null;
        setSelectedPatient(pat);
      } else {
        setSelectedPatient(null);
      }
      setModalOpen(true);
    },
    [patients]
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedInvoice(null);
    setSelectedPatient(null);
    fetchInvoices();
  }, [fetchInvoices]);

  return (
    <Stack gap="md" className="p-6">
      <Group justify="space-between">
        <Group gap="sm">
          <IconCash size={24} />
          <Title order={2}>
            {t('paymentsPage.title', 'Payments', { ns: 'lims' })}
          </Title>
        </Group>
      </Group>

      <PaymentSummaryCards invoices={invoices} />

      <ResourceSearchForm<PaymentSearchParams>
        fields={fields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        clearLabel={t('paymentsPage.clear', 'Clear', { ns: 'lims' })}
        layout={{ cols: { base: 1, sm: 2, lg: 4 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : (
          <ResourceTable<Invoice>
            data={invoices}
            columns={columns}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowClick={handleInvoiceClick}
            onActionClick={handleInvoiceClick}
            emptyText={t(
              'paymentsPage.noPaymentsFound',
              'No payments found',
              {
                ns: 'lims',
              }
            )}
          />
        )}
      </Paper>

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        opened={modalOpen}
        onClose={handleModalClose}
        invoice={selectedInvoice}
        patient={selectedPatient}
      />
    </Stack>
  );
};
